import BoatFill from '@/assets/icons/boat_fill.svg?component';
import FishPenLine from '@/assets/icons/fish_pen_line.svg?component';
import InformationLine from '@/assets/icons/information_line.svg?component';
import type { ConfirmRegistrationItem } from '@/components/organisms/FishGroupForms';
import { getIsoWeek } from '@/utils/date';

import { type BioscopeOutcome, bioscopeOutcomeItems } from './bioscopeOutcomes';
import type { Translate } from './penOptions';

interface MoveSummary {
    date: Date;
    fromPen: string;
    toPen: string;
    /** The group's bioscopes: those that move with it, or stay behind. */
    bioscopeIds: number[];
    /** Bioscopes already in the destination pen; they measure the group there too. */
    targetBioscopeIds: number[];
    /** The bioscopes go along with the fish. */
    bioscopesMove: boolean;
    withDelousing: boolean;
}

/** What a move will show once confirmed: the board marker, the bioscope, the pen left behind. */
export function moveConfirmItems(summary: MoveSummary, t: Translate): ConfirmRegistrationItem[] {
    const { date, fromPen, toPen, bioscopeIds, targetBioscopeIds, bioscopesMove, withDelousing } =
        summary;
    const outcomes: BioscopeOutcome[] = [];
    if (targetBioscopeIds.length > 0) {
        outcomes.push({ kind: 'measures', ids: targetBioscopeIds, pen: toPen, moved: true });
    }
    if (bioscopesMove && bioscopeIds.length > 0) {
        outcomes.push({ kind: 'movesIn', ids: bioscopeIds, from: fromPen, pen: toPen });
    }
    if (outcomes.length === 0) outcomes.push({ kind: 'none', pen: toPen });
    if (!bioscopesMove && bioscopeIds.length > 0) {
        outcomes.push({ kind: 'staysBehind', ids: bioscopeIds, pen: fromPen });
    }
    const items: ConfirmRegistrationItem[] = [
        {
            key: 'timeline',
            icon: InformationLine,
            title: t('siteActions.confirm.timeline'),
            text: t('siteActions.confirm.moveMarker', { week: getIsoWeek(date) }),
        },
        ...bioscopeOutcomeItems(outcomes, t),
        {
            key: 'pen',
            icon: FishPenLine,
            title: fromPen,
            text: t('siteActions.confirm.penEmptied'),
        },
    ];
    if (withDelousing) {
        items.push({
            key: 'delousing',
            icon: BoatFill,
            title: t('siteActions.detail.actions.delousing'),
            text: t('siteActions.confirm.delousingWithMove'),
        });
    }
    return items;
}

/** What a delousing will show once confirmed: the board badge, the treatment, the group. */
export function delousingConfirmItems(
    summary: { date: Date; groupName: string; penName: string; treatmentMethod: string },
    t: Translate
): ConfirmRegistrationItem[] {
    const method = summary.treatmentMethod.trim();
    return [
        {
            key: 'timeline',
            icon: InformationLine,
            title: t('siteActions.confirm.timeline'),
            text: t('siteActions.delousing.marker', { week: getIsoWeek(summary.date) }),
        },
        {
            key: 'treatment',
            icon: BoatFill,
            title: t('siteActions.form.treatment'),
            text: method || t('siteActions.form.treatmentMethodPlaceholder'),
        },
        {
            key: 'group',
            icon: FishPenLine,
            title: summary.penName,
            text: t('siteActions.delousing.wholeGroup', { name: summary.groupName }),
        },
    ];
}
